/**
 * Shared utilities for Acknowledgment Workflow Modals
 *
 * Contains the useReasonCodes hook, ReasonCodeSelect component,
 * and getEffectiveReason helper function.
 */

import { useState, useEffect } from 'react';
import { purchasingAPI, ReasonCode } from '../../lib/api';

/**
 * Shared hook to fetch reason codes from the API.
 * Caches results for the component lifecycle.
 */
export function useReasonCodes() {
  const [reasonCodes, setReasonCodes] = useState<ReasonCode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    purchasingAPI.getReasonCodes({ is_active: true, page_size: 100 })
      .then(res => {
        if (!cancelled) setReasonCodes(res.data.results);
      })
      .catch(err => console.error('Failed to load reason codes:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { reasonCodes, loading };
}

/**
 * Reusable reason code select dropdown with "Other (custom)" option.
 */
export function ReasonCodeSelect({
  value,
  onChange,
  customValue,
  onCustomChange,
  required = false,
  label = 'Reason',
}: {
  value: string;
  onChange: (val: string) => void;
  customValue: string;
  onCustomChange: (val: string) => void;
  required?: boolean;
  label?: string;
}) {
  const { reasonCodes, loading } = useReasonCodes();

  // Group by category
  const grouped = reasonCodes.reduce<Record<string, ReasonCode[]>>((acc, rc) => {
    const cat = rc.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(rc);
    return acc;
  }, {});

  const categoryLabels: Record<string, string> = {
    delay: 'Delay',
    delivery: 'Delivery',
    quality: 'Quality',
    quantity: 'Quantity',
    price: 'Pricing',
    pricing: 'Pricing',
    technical: 'Technical',
    customer: 'Customer',
    supplier: 'Supplier',
    product: 'Product',
    documentation: 'Documentation',
    packaging: 'Packaging',
    other: 'Other',
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required && value !== '__custom__'}
        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <option value="">Select a reason...</option>
        {loading ? (
          <option disabled>Loading reason codes...</option>
        ) : (
          Object.entries(grouped).map(([category, codes]) => (
            <optgroup key={category} label={categoryLabels[category] || category}>
              {codes.map(rc => (
                <option key={rc.id} value={rc.description}>
                  {rc.code}: {rc.description}
                </option>
              ))}
            </optgroup>
          ))
        )}
        <optgroup label="Custom">
          <option value="__custom__">Other (enter custom reason)</option>
        </optgroup>
      </select>
      {value === '__custom__' && (
        <textarea
          value={customValue}
          onChange={(e) => onCustomChange(e.target.value)}
          rows={2}
          required={required}
          placeholder="Enter your reason..."
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        />
      )}
    </div>
  );
}

/** Helper: resolve the effective reason string from select value + custom text */
export function getEffectiveReason(selectValue: string, customText: string): string {
  if (selectValue === '__custom__') return customText.trim();
  return selectValue;
}

export interface WorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineId: number;
  lineNumber: number;
  partNumber: string;
  orderNumber?: string;
  currentDeliveryDate: string;
  // CR-4729: Customer's proposed promise date — when present, the Accept /
  // Decline modals render "From <currentDeliveryDate> to <proposedDate>" so
  // the user clearly sees the change they're confirming, not just the
  // existing date.
  proposedDate?: string;
  onSubmit: (data: { action: string; lineId: number; comments?: string; reason?: string; proposedDate?: string; proposedQuantity?: number; proposedPrice?: number; files?: File[] }) => Promise<void>;
}
